import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { HistoryService } from '@fi-app/history/history.service';
import { IHistoryModel } from '@fi-app/history/history';
import { IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { UserService } from '@fi-app/user/user.service';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { getObjectViewStateData } from '@fi-shared/utils/get-object-view-state-data';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';

@Component({
    selector: 'fi-history-list',
    templateUrl: './history-list.component.html',
    styleUrls: ['./history-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-list' },
    standalone: false,
})
export class HistoryListComponent extends FiRemoteStateFullDataSource<IHistoryModel> implements OnInit {
    public defaultSortDirection = false;
    public defaultSortColumnName: string = 'CreatedOn';
    public readonly itemsListType = FiItemsListType.HistoryList;
    public readonly objectType = ObjectTypes.History;

    private historyService = inject(HistoryService);
    private userService = inject(UserService);
    private businessUnitService = inject(BusinessUnitService);
    private fiTaskV2HelperService = inject(FiTaskV2HelperService);

    private readonly notAvailableObjectViewers: ReadonlyArray<ActionType> = [
        ActionType.Delete,
        ActionType.MoveToTrash,
        ActionType.CreateAccessRequest,
        ActionType.RejectAccessRequest,
        ActionType.CheckPointSetApplicable,
        ActionType.CheckPointSetNotApplicable,
    ];

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.reloadData();
    }

    protected openUserProfileDialog(item: IHistoryModel): void {
        if (isDefined(item.CreatedByUserId)) {
            this.userService.openUserViewDialog(item.CreatedByUserId);
        }
    }

    protected redirectToObjectViewer(item: IHistoryModel): void {
        let objectId: number;
        let objectType: ObjectTypes;
        let isHistoryItemAvailable: boolean;
        if (item.ObjectType === ObjectTypes.Checkpoint2 || item.ObjectType === ObjectTypes.Checkpoint2Audit) {
            // special case for checkpoints of checklists v2;
            objectId = item.AdditionalData.СhecklistToScheduleId;
            objectType = item.AdditionalData.ChecklistToScheduleObjectType;
            isHistoryItemAvailable = true;
        } else {
            objectId = item.ObjectId;
            objectType = item.ObjectType;
            isHistoryItemAvailable = this.isHistoryItemAvailable(item);
        }

        const srefData = getObjectViewStateData(objectType, objectId, {
            taskV2Mode: this.fiTaskV2HelperService.getNormalizedTaskV2(),
        });

        if (!isHistoryItemAvailable || !srefData) return;

        if (item.ObjectType === ObjectTypes.User) {
            // when we click on object name we should use ObjectId as userId
            // https://chainformation.atlassian.net/browse/FI-5469
            this.userService.openUserViewDialog(item.ObjectId);
            return;
        }
        if (item.ObjectType === ObjectTypes.BusinessUnit) {
            // when we click on object name we should use ObjectId as business unit id
            // https://chainformation.atlassian.net/browse/FI-6800
            this.businessUnitService.openBusinessUnitViewDialog(item.ObjectId);
            return;
        }

        this.fiStateService.go(srefData.sref, srefData.srefParams);
    }

    private isHistoryItemAvailable(item: IHistoryModel): boolean {
        if (item.ActionType === ActionType.MoveToTrash && item.ObjectType == ObjectTypes.Task) {
            // special case for tasks
            return true;
        }
        return (
            this.notAvailableObjectViewers.indexOf(item.ActionType) === -1 &&
            !(item.ChildItems && item.ChildItems.length)
        );
    }

    protected getObjectTypeLabel(item: IHistoryModel): string {
        let text = this.appTextService.get(getObjectTypeNameTextId(item.ObjectType));
        if ([ObjectTypes.ChecklistSchedule, ObjectTypes.Checkpoint2].includes(item.ObjectType)) text = text + ' 2.0';
        return text;
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: false, // because of huge amount of local search data when searchText is empty we should
            // request local search data only when searchText is not empty
            //FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        parameter.Offset = loadArgs.fetchParams.offset;
        parameter.NumberOfRecordsToTake = loadArgs.fetchParams.numberOfRecordsToTake;
        return this.historyService.getHistoryListData(parameter);
    }

    private _processingSearchTextChanged = false;
    protected onSearchTextChanged(searchText: string): Promise<void> {
        if (searchText.length >= 1 && !this._processingSearchTextChanged) {
            this._processingSearchTextChanged = true;
            const parameter: IItemsListParameterBase = {
                ContentSearchTexts: [searchText],
            };
            return this.historyService.getLightWeightObjectsForLocalSearch(parameter).then((result) => {
                this.setLightWeightObjectCollection(result);
                this._processingSearchTextChanged = false;
            });
        }
    }
}
